import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, Plus, Trash2, X, Calendar, Users } from "lucide-react";
import { api } from "../lib/api";
import { CONTEST_STATUS, fmtScore } from "./RewardsVendor";

const METRIC_OPTIONS = [
  ["ca", "Chiffre d'affaires (prix usine)"],
  ["orders", "Nombre de commandes"],
  ["new_clients", "Nombre de nouveaux clients"],
  ["product_qty", "Ventes d'une référence"],
];

const fmtDate = (d) => { try { const [y, m, dd] = d.split("-"); return `${dd}/${m}/${y}`; } catch { return d; } };

export const RewardsAdmin = () => {
  const [overview, setOverview] = useState(null);
  const [contests, setContests] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "", metric: "ca", catalog_id: "", prizes: [""] });

  const load = () => {
    api.get("/admin/rewards/overview").then((r) => setOverview(r.data)).catch(console.error);
    api.get("/admin/contests").then((r) => setContests(r.data)).catch(console.error);
    api.get("/admin/catalog").then((r) => setCatalog(r.data)).catch(console.error);
  };
  useEffect(() => { load(); }, []);

  const createContest = async () => {
    try {
      await api.post("/admin/contests", { ...form, catalog_id: form.catalog_id || null, prizes: form.prizes.filter((p) => p.trim()) });
      toast.success("Jeu concours lancé 🏆");
      setForm({ name: "", start_date: "", end_date: "", metric: "ca", catalog_id: "", prizes: [""] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleContest = async (c) => { await api.patch(`/admin/contests/${c.id}`, { active: !c.active }); load(); };
  const deleteContest = async (c) => {
    if (!window.confirm(`Supprimer le jeu "${c.name}" ?`)) return;
    await api.delete(`/admin/contests/${c.id}`); load(); toast.success("Jeu supprimé");
  };
  const redeemPoints = async (v) => {
    const gift = window.prompt(`Cadeau remis à ${v.vendor_name} (solde : ${v.points} pts) :`, "");
    if (gift === null || !gift.trim()) return;
    const pts = window.prompt(`Nombre de points à déduire pour "${gift}" :`, "");
    const points = parseInt(pts, 10);
    if (!points || points <= 0) return;
    try {
      await api.post("/admin/rewards/redeem", { vendor_id: v.vendor_id, points, gift });
      toast.success(`${points} pts déduits — cadeau : ${gift}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const setPrize = (i, v) => setForm((f) => ({ ...f, prizes: f.prizes.map((p, j) => (j === i ? v : p)) }));

  if (!overview) return <p className="text-zinc-500 py-10 text-center">Chargement...</p>;

  return (
    <div className="space-y-8">
      <section data-testid="single-remuneration-note" className="bg-ink-surface border border-neon-green/40 p-4">
        <h2 className="font-anton text-xl uppercase text-white mb-1">Rémunération unique : <span className="text-neon-green">50 % du HT</span> pour tous les vendeurs</h2>
        <p className="text-zinc-500 text-xs">Les statuts Junior / Senior / Or sont supprimés. Prix usine = 50 % du HT de chaque produit (recalculé automatiquement dans le Catalogue), le vendeur garde l'autre moitié + la livraison HT.</p>
      </section>

      {/* Points annuels */}
      <section>
        <h2 className="font-anton text-xl uppercase text-white mb-3">Programme récompenses {overview.year} <span className="text-zinc-500 text-sm font-sans normal-case">(1€ prix usine livré = 1 point, parrainage exclu)</span></h2>
        <div className="bg-ink-surface border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-zinc-600 text-xs uppercase text-left border-b border-zinc-800"><th className="p-3">Vendeur</th><th>Ville</th><th className="text-right">Sem. en cours</th><th className="text-right">CA {overview.year}</th><th className="text-right">Solde</th><th className="text-right pr-3"></th></tr></thead>
            <tbody>
              {overview.vendors.map((v) => (
                <tr key={v.vendor_id} data-testid="rewards-vendor-row" className="border-b border-zinc-800/60 text-zinc-300">
                  <td className="p-3 font-bold text-white">{v.vendor_name}</td>
                  <td className="text-zinc-500">{v.city}</td>
                  <td className="text-right">{Number(v.current_week_ca || 0).toFixed(2)}€</td>
                  <td className="text-right" title={`Gagnés ${v.points_earned} · Échangés ${v.points_spent}`}>{v.ca.toFixed(2)}€</td>
                  <td className="text-right text-neon-green font-bold">{v.points} pts</td>
                  <td className="text-right pr-3">
                    <button data-testid="redeem-points-btn" onClick={() => redeemPoints(v)} disabled={v.points <= 0} className="text-xs uppercase font-bold border border-neon-magenta text-neon-magenta px-2 py-1 disabled:opacity-30 disabled:cursor-not-allowed">🎁 Cadeau</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {overview.redemptions.length > 0 && (
          <div className="mt-3">
            <p className="text-zinc-500 text-xs uppercase mb-2">Derniers cadeaux échangés</p>
            <div className="bg-ink-surface border border-zinc-800">
              {overview.redemptions.map((r) => (
                <div key={r.id} data-testid="admin-redemption-row" className="flex items-center justify-between px-4 py-2 border-b border-zinc-800/60 text-sm">
                  <span className="text-white">{r.vendor_name} — 🎁 {r.gift}</span>
                  <span className="text-zinc-500 text-xs">{fmtDate(r.created_at.slice(0, 10))}</span>
                  <span className="text-neon-magenta font-bold">-{r.points} pts</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Jeux concours */}
      <section>
        <h2 className="font-anton text-xl uppercase text-white mb-3 flex items-center gap-2"><Trophy className="h-5 w-5 text-neon-magenta" /> Jeux concours</h2>
        <div className="bg-ink-surface border border-neon-magenta/30 p-4 mb-4">
          <p className="text-zinc-500 text-xs uppercase mb-3">Lancer un jeu (commandes livrées uniquement, parrainage exclu)</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <input data-testid="contest-name-input" placeholder="Nom du jeu (ex: Challenge du mois)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
            <input data-testid="contest-start-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
            <input data-testid="contest-end-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-zinc-500 text-xs uppercase block mb-1">Critère de classement</label>
              <select data-testid="contest-metric-select" value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta">
                {METRIC_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {form.metric === "product_qty" && (
              <div>
                <label className="text-zinc-500 text-xs uppercase block mb-1">Référence produit</label>
                <select data-testid="contest-product-select" value={form.catalog_id} onChange={(e) => setForm({ ...form, catalog_id: e.target.value })} className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta">
                  <option value="">— Choisir un produit —</option>
                  {catalog.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.category})</option>)}
                </select>
              </div>
            )}
          </div>
          <p className="text-zinc-500 text-xs uppercase mb-2">Cadeaux par rang (le nombre de lignes = nombre de vendeurs récompensés)</p>
          {form.prizes.map((p, i) => (
            <div key={i} className="flex items-center gap-2 mb-2">
              <span className="text-zinc-500 text-sm w-12">{i + 1}er :</span>
              <input data-testid={`contest-prize-input-${i}`} placeholder={i === 0 ? "ex: iPhone" : "ex: 50€ de bon"} value={p} onChange={(e) => setPrize(i, e.target.value)} className="flex-1 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
              {form.prizes.length > 1 && <button onClick={() => setForm((f) => ({ ...f, prizes: f.prizes.filter((_, j) => j !== i) }))} className="p-2 text-zinc-500 hover:text-red-500"><X className="h-4 w-4" /></button>}
            </div>
          ))}
          <div className="flex gap-2">
            <button data-testid="contest-add-prize" onClick={() => setForm((f) => ({ ...f, prizes: [...f.prizes, ""] }))} className="flex items-center gap-1 text-xs uppercase font-bold border border-zinc-700 text-zinc-300 px-3 py-2"><Plus className="h-4 w-4" /> Ajouter un cadeau</button>
            <button data-testid="contest-create-btn" onClick={createContest} className="flex items-center gap-1 text-xs uppercase font-bold bg-neon-green text-black px-4 py-2 glow-green"><Trophy className="h-4 w-4" /> Lancer le jeu</button>
          </div>
        </div>

        {contests.length === 0 ? (
          <p className="text-zinc-500 py-6 text-center border border-dashed border-zinc-800">Aucun jeu concours créé.</p>
        ) : (
          <div className="space-y-3">
            {contests.map((c) => {
              const st = CONTEST_STATUS[c.status] || CONTEST_STATUS.termine;
              return (
                <div key={c.id} data-testid="contest-row" className="bg-ink-surface border border-zinc-800 p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="font-anton text-lg text-white uppercase">{c.name}</span>
                    <span className={`text-xs uppercase border px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                    <span className="text-xs uppercase border border-neon-magenta/50 text-neon-magenta px-2 py-0.5">{(c.metric_label || "CA prix usine")}{c.product_name ? ` : ${c.product_name}` : ""}</span>
                    {!c.active && <span className="text-xs uppercase border border-red-400 text-red-400 px-2 py-0.5">Désactivé</span>}
                    <span className="text-zinc-500 text-xs flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(c.start_date)} → {fmtDate(c.end_date)}</span>
                    <span className="text-zinc-500 text-xs flex items-center gap-1"><Users className="h-3 w-3" /> {c.leaderboard.length} classé{c.leaderboard.length > 1 ? "s" : ""}</span>
                    <div className="ml-auto flex gap-2">
                      <button data-testid="contest-toggle" onClick={() => toggleContest(c)} className={`text-xs uppercase font-bold border px-3 py-1.5 ${c.active ? "border-zinc-600 text-zinc-400" : "border-neon-green text-neon-green"}`}>{c.active ? "Désactiver" : "Activer"}</button>
                      <button data-testid="contest-delete" onClick={() => deleteContest(c)} className="p-1.5 text-zinc-500 hover:text-red-500 border border-zinc-700"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <p className="text-zinc-500 text-xs mb-2">Cadeaux : {c.prizes.map((p, i) => `${i + 1}er → ${p}`).join(" · ")}</p>
                  {c.leaderboard.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead><tr className="text-zinc-600 text-xs uppercase text-left"><th className="py-1">#</th><th>Vendeur</th><th>Ville</th><th className="text-right">Score</th><th className="text-right">Cadeau</th></tr></thead>
                      <tbody>
                        {c.leaderboard.map((r) => (
                          <tr key={r.vendor_id} className="border-t border-zinc-800 text-zinc-300">
                            <td className="py-1.5">{r.rank}</td><td>{r.vendor_name}</td><td className="text-zinc-500">{r.city}</td>
                            <td className="text-right">{fmtScore(c.metric, r.score)}</td>
                            <td className="text-right text-neon-magenta">{r.prize || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <p className="text-zinc-600 text-xs">Aucune vente livrée sur la période pour l'instant.</p>}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};
